import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { MAddress } from '../../user/models/address';
import { MEmergencyContact } from '../../user/models/emergency-contact';
import { userService } from '../../user/services';

type TState = {
  address: MAddress[];
  emergencyContact: MEmergencyContact[];
  selectedAddress: MAddress | null | undefined;
  currentLat: number | undefined;
  currentLong: number | undefined;
};

type TAction = {
  setAddress: (addresses: MAddress[]) => void;
  getAddressById: (id: number | string | null) => MAddress | undefined;
  resetState: () => void;
  setSelectedAddress: (
    id: number | string | null,
    individualId: number | undefined,
    userId: number | undefined
  ) => void;
  deleteAddress: (id: number | string) => void;
  setCoordinate: (lat: number | undefined, long: number | undefined) => void;

  setEmergencyContact: (contacts: MEmergencyContact[]) => void;
  getEmergencyContactById: (
    id: number | string | null
  ) => MEmergencyContact | undefined;
  deleteEmergencyContact: (id: number | string) => void;
};

export const useAddressStore = create<TState & TAction>()(
  immer((set, get) => ({
    address: [],
    emergencyContact: [],
    selectedAddress: null,
    currentLat: undefined,
    currentLong: undefined,
    setAddress: (addresses: MAddress[]) =>
      set((state) => {
        state.address = addresses;
        const selected =
          addresses.find((addr) => addr.is_main_address === true) ?? null;
        state.selectedAddress = selected;
        if (selected?.latitude_position && selected?.longitude_position) {
          state.currentLat = selected.latitude_position;
          state.currentLong = selected.longitude_position;
        }
      }),
    getAddressById: (id: number | string | null) => {
      const addresses = get().address;
      return addresses?.find((addr) => addr.id === Number(id));
    },
    resetState: () => {
      set((state) => {
        state.address = [];
        state.emergencyContact = [];
        state.selectedAddress = null;
        state.currentLat = undefined;
        state.currentLong = undefined;
      });
    },
    setSelectedAddress: (
      id: number | string | null,
      individualId: number | undefined,
      userId: number | undefined
    ) => {
      const numericId = Number(id);
      const address = get().getAddressById(numericId);

      if (!address) {
        return;
      }

      userService.updateMainAddress({ address, individualId, userId });

      set((state) => {
        state.address.forEach((addr) => {
          addr.is_main_address = addr.id === numericId;
        });

        state.selectedAddress =
          state.address.find((addr) => addr.id === numericId) ?? null;
      });
    },
    deleteAddress: (id: number | string) => {
      set((state) => {
        state.address = state.address.filter((a) => a.id !== Number(id));
        if (state.selectedAddress?.id === Number(id)) {
          state.selectedAddress = state.address[0] ?? null;
        }
      });
    },
    setCoordinate(lat: number | undefined, long: number | undefined) {
      if (!lat || !long) {
        return;
      }
      set((state) => {
        if (!state.selectedAddress && lat && long) {
          state.currentLong = long;
          state.currentLat = lat;
        }
      });
    },

    setEmergencyContact: (contacts: MEmergencyContact[]) =>
      set((state) => {
        state.emergencyContact = contacts;
      }),

    getEmergencyContactById: (id) => {
      return get().emergencyContact?.find(
        (contact) => contact.id === Number(id)
      );
    },

    deleteEmergencyContact: (id: number | string) => {
      set((state) => {
        state.emergencyContact = state.emergencyContact.filter(
          (c) => c.id !== Number(id)
        );
      });
    },
  }))
);
